import { translateKey } from '@helpers/resolveTranslationsKey';
import { getCampaignSourceId } from '@services/domain-factories/campaigns';
import { expressionsService } from '@services/domain-factories/expressions';
import { formGroupsService } from '@services/domain-factories/formGroups';
import { formModelsService } from '@services/domain-factories/formModels';
import { formsService } from '@services/domain-factories/forms';
import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { accountStore } from '@stores/bootstrap-stores/account';
import { appUserStore } from '@stores/bootstrap-stores/user';
import { hasItems } from '@utils/vanillaJs';
import {
  Campaign,
  ContentForm,
  Expression,
  FormGroup,
  FormModel,
  InvitationEnvelope,
  Member,
  SurveyResponse
} from '@vincerosoft/assessfluence-types';
import { limit, QueryFieldFilterConstraint, where } from 'firebase/firestore';

export type FormGroupAssociatedData = {
  byCampaignId: {
    [campaignId: string]: {
      campaign: Campaign;
      invitation: InvitationEnvelope;
      responses: SurveyResponse[];
    };
  };
  formGroup: FormGroup;
  forms: ContentForm[];
  formModels: FormModel[];
  expressions: Expression[] | null;
};

function learningCenterFactory() {
  const whereEntity = (entityId: string) => where('entity_id', '==', entityId);
  const whereOwnedBy = (memberId: string, userId: string) =>
    where('owned_by', 'in', [memberId, userId]);

  async function loadLearningCenterData(options?: { fromDate?: Date; limit?: number }) {
    const inFlightStore: {
      byFormGroupId: {
        [formGroupId: string]: FormGroupAssociatedData;
      };
    } = { byFormGroupId: {} };
    const formGroupGrants = accountStore.getState().resourceGrants?.formGroups || [];
    const formGroupIds = formGroupGrants.map((fg) => fg.resource_id);

    if (formGroupIds.length === 0) {
      return inFlightStore;
    }

    const formGroupBatches = await collectionsService.formGroups.getDocsInBatches<FormGroup>(
      ['id', 'in', formGroupIds],
      [where('intent', '==', 'content')]
    );

    const formIds: Set<string> = new Set();

    const formGroups = formGroupBatches.flat();
    formGroups.forEach((formGroup) => {
      const linkedFormIds = formGroupsService.extractLinkedFormIds([formGroup]);
      linkedFormIds.forEach((formId) => formIds.add(formId));
      inFlightStore.byFormGroupId[formGroup.id] = {
        byCampaignId: {},
        formGroup,
        forms: [],
        formModels: [],
        expressions: []
      };
    });

    await formsService.getFormsByIds(
      'learningCenterFactory',
      Array.from(formIds),
      undefined,
      (data) => {
        data.forEach((form) => {
          const formGroupId = formGroups.find((gr) =>
            (gr.form_stacks || []).some(
              (stack) => Array.isArray(stack?.form_ids) && stack.form_ids.includes(form.id)
            )
          )?.id;
          if (formGroupId && inFlightStore.byFormGroupId[formGroupId]) {
            inFlightStore.byFormGroupId[formGroupId].forms!.push({
              ...form,
              name: translateKey(form.name),
              description: translateKey(form.description!)
            } as ContentForm);
          }
        });
      }
    );

    const expressions = await expressionsService.getRuntimeExpressionsBySourceIds(
      Array.from(formIds)
    );
    Object.values(inFlightStore.byFormGroupId).forEach((entry) => {
      entry.expressions = expressions;
    });

    const formModels = await formModelsService.getFormModelsByIds(Array.from(formIds));
    formModels.forEach((formModel) => {
      const formModelInferred = formModel as FormModel & { form_id: string };
      const formGroupEntry = Object.values(inFlightStore.byFormGroupId).find((entry) =>
        (entry.forms || []).some((f) => f.id === formModelInferred.form_id)
      );
      if (formGroupEntry) {
        formGroupEntry.formModels.push(formModel);
      }
    });

    const campaignBatches = await getCampaigns(formGroupIds, options);
    campaignBatches.flat().forEach((campaign) => {
      const formGroupId = getCampaignSourceId(campaign);
      if (inFlightStore.byFormGroupId[formGroupId]) {
        inFlightStore.byFormGroupId[formGroupId].byCampaignId[campaign.id] = {
          campaign,
          invitation: {} as InvitationEnvelope,
          responses: []
        };
      }
    });

    const campaigns = campaignBatches.flat();
    const campaignIds = campaigns.map((c) => c.id);

    const invitationsBatches = await collectionsService.invitations.getDocsInBatches([
      'linked_campaign.id',
      'in',
      campaignIds
    ]);

    const invitations = invitationsBatches.flat();

    invitations.forEach((invitation) => {
      const campaignId = invitation.linked_campaign.id;
      const campaign = campaigns.find((c) => c.id === campaignId);
      if (!campaign) return;
      const formGroupId = getCampaignSourceId(campaign);
      if (
        formGroupId &&
        inFlightStore.byFormGroupId[formGroupId] &&
        inFlightStore.byFormGroupId[formGroupId].byCampaignId[campaignId]
      ) {
        inFlightStore.byFormGroupId[formGroupId].byCampaignId[campaignId].invitation = invitation;
      }
    });

    const responsesInBatches = await collectionsService.responses.getDocsInBatches([
      'campaign.id',
      'in',
      campaignIds
    ]);
    const responses = responsesInBatches.flat();
    responses.forEach((response) => {
      const campaignId = response.campaign!.id;
      const campaign = campaigns.find((c) => c.id === campaignId);
      if (!campaign) return;
      const formGroupId = getCampaignSourceId(campaign);
      if (
        formGroupId &&
        inFlightStore.byFormGroupId[formGroupId] &&
        inFlightStore.byFormGroupId[formGroupId].byCampaignId[campaignId]
      ) {
        inFlightStore.byFormGroupId[formGroupId].byCampaignId[campaignId].responses.push(response);
      }
    });

    return inFlightStore;
  }

  async function getTaxonomyData(formGroups: FormGroup[]) {
    const { catIds, tagIds } = hasItems(formGroups)
      ? formGroups.reduce<{
          catIds: string[];
          tagIds: string[];
        }>(
          (acc, gr) => {
            if (gr?.options?.category) {
              acc.catIds.push(gr?.options?.category);
            }
            if (gr?.options?.tags) {
              acc.tagIds.push(...gr.options.tags);
            }
            return acc;
          },
          {
            catIds: [],
            tagIds: []
          }
        )
      : { catIds: [], tagIds: [] };

    const categories =
      catIds.length > 0
        ? (await collectionsService.categories.getDocsInBatches(['id', 'in', catIds], [])).flat()
        : [];

    const tags =
      tagIds.length > 0
        ? (await collectionsService.tags.getDocsInBatches(['id', 'in', tagIds], [])).flat()
        : [];

    return { categories, tags };
  }

  async function fetchMemberCampaignCount(formGroupIds: string[]) {
    const member = appUserStore.getState().currentMember! as Member & {
      entity_id: string;
    };
    const query = [whereEntity(member.entity_id), whereOwnedBy(member.id, member.user_id)];

    return collectionsService.campaigns.getCountInBatches(
      ['resource.id', 'in', formGroupIds],
      [...query]
    );
  }

  async function getCampaigns(
    formGroupIds: string[],
    options?: { fromDate?: Date; limit?: number }
  ) {
    const member = appUserStore.getState().currentMember! as Member & {
      entity_id: string;
    };
    const query = [whereEntity(member.entity_id), whereOwnedBy(member.id, member.user_id)];

    if (options?.fromDate) {
      query.push(where('created_at', '>=', options.fromDate));
    }
    if (options?.limit) {
      query.push(limit(options.limit) as unknown as QueryFieldFilterConstraint);
    }

    const campaigns = await collectionsService.campaigns.getDocsInBatches<
      Campaign & {
        created_at: Date;
      }
    >(['resource.id', 'in', formGroupIds], [...query]);
    return campaigns.flat();
  }

  return {
    loadLearningCenterData,
    fetchMemberCampaignCount,
    getCampaigns,
    getTaxonomyData
  };
}

export const learningCenterService = learningCenterFactory();
